import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ActiveChipClose,
  ActiveFiltersBar,
  ApiError,
  AskAnswerSummary,
  AskTheRoomPrompt,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataRoomFolders,
  documentInPhase,
  folderPhaseView,
  type FolderPhaseGroup,
  DEFAULT_DOCUMENT_SORT,
  DocumentList,
  HelpTip,
  Icon,
  NO_DATA_ROOM_FILTERS,
  SYNTHETIC_CATEGORY_IDS,
  DataRoomFilterMenu,
  SearchBar,
  StatusChip,
  UnreadableFilterChip,
  UnreadableFileOptions,
  documentDownloadPath,
  ResultsTitle,
  askAnswerDocuments,
  branchCategoryIds,
  categoryNodesById,
  categoryPathNames,
  categoryParents,
  cn,
  countStatementBases,
  dataRoomFilterChips,
  dataRoomFiltersFrom,
  dataRoomSearch,
  describePeriodKey,
  documentSortFrom,
  documentStatusKey,
  folderViewTree,
  hasDataRoomFilters,
  hasSearchCriteria,
  narrowDataRoomDocuments,
  periodNodesByCategory,
  sortDocuments,
  withDocumentSort,
  type DataRoomFilters,
  type DocumentListRow,
  type DocumentSort,
  type RowMenuItem,
  useAskTheRoom,
  useDataRoomSearchBox,
  FileIndicatorsView,
  fileIndicatorsOf,
} from '@dataroom/shared';
import type {
  CategoryNode,
  ClassificationStatus,
  RoomDocument,
  StatementBasis,
} from '@dataroom/shared';
import {
  useApproveProposal,
  useDocumentSearch,
  useModifyDocumentAttributes,
  useSetDocumentVisibility,
  useSetStatementBasis,
  usePendingProposals,
  useReclassifyDocument,
  useClearUnreadableFlag,
  useUploadReplacement,
  useReclassifyNeedsReview,
  useRejectProposal,
  useRoomCategories,
  useRoomFolderOrder,
  useRoomDocuments,
} from '@/api/hooks';
import { FolderAudienceSummary } from '@/components/ChangeAudienceDialog';
import { DocumentDetailModal } from '@/components/DocumentDetailModal';
import { VersionSuggestionsPanel } from '@/components/VersionSuggestionsPanel';
import { VersionSuggestionsReview } from '@/components/VersionSuggestionsReview';
import { DocumentVersionHistory } from '@/components/DocumentVersionHistory';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import { ManageDocumentDialog } from '@/components/ManageDocumentDialog';
import { redactionReviewPath } from '@/lib/redaction-review';
import { ModifyDocumentAttributesDialog } from '@/components/ModifyDocumentAttributesDialog';
import { NewFolderDialog } from '@/components/NewFolderDialog';
import { useGuideTarget, type GuideTarget } from '@/lib/guide-target';
import { RoomProcessingEstimate } from '@/components/RoomProcessingEstimate';
import { UploadDocumentsCard } from '@/components/UploadDocumentsCard';
import { isUnauthorized } from '@/lib/checklist';
import { visibilityMenuItems } from '@/lib/document-visibility';
import {
  countNeedsReview,
  isNeedsReview,
  type NeedsReviewCounts,
} from '@/lib/classification-review';
import { useRoomContext } from './context';

function apiMessage(err: unknown, fallback: string): string | null {
  if (!err) return null;
  return err instanceof ApiError ? err.message : fallback;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** Bring `section` to the top of the screen (smoothly unless the viewer asked
 * for reduced motion) and focus its first enabled control. */
function revealSection(section: HTMLElement) {
  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Focus first: in Chrome a focus() — even with preventScroll — cancels a
  // smooth scroll already under way.
  section
    .querySelector<HTMLElement>('button:not([disabled]), input:not([disabled])')
    ?.focus({ preventScroll: true });
  section.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
}

/**
 * The room's default view — the actual DATA ROOM (owner ask #3): the category
 * tree on the left, the documents on the right (search, classification badges,
 * Review / Modify attributes), the in-app viewer on click, and "Add documents"
 * as a button that opens the upload panel rather than a permanently expanded
 * card.
 *
 * Classification review (ADR-0018): a low-confidence / couldn't-classify badge
 * (and that row's name) opens the review panel; "Needs review (N)" narrows the
 * list to those documents; "Re-run AI on N documents" re-classifies them all.
 */
export function RoomDataPage() {
  const { roomId: id } = useRoomContext();
  const documents = useRoomDocuments(id);
  const navigate = useNavigate();
  // "Review redaction" (owner, 2026-10-15): every redaction that needs a person
  // opens the redaction review screen for that document.
  const reviewRedaction = (documentId: string) => navigate(redactionReviewPath(id, documentId));
  const categories = useRoomCategories(id);
  const folderOrder = useRoomFolderOrder(id);
  // Everything that narrows the list — folder, period, search, "Needs a look" /
  // "Couldn't read", accounting basis — lives in the URL (lib/data-room-filters),
  // so Back returns to the view before it and a filtered view can be linked.
  // Applying a filter adds a history entry; refining the search text replaces it.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = dataRoomFiltersFrom(searchParams);
  const setFilters = (patch: Partial<DataRoomFilters>, replace = false) =>
    setSearchParams((prev) => dataRoomSearch({ ...dataRoomFiltersFrom(prev), ...patch }, prev), {
      replace,
    });
  // The list's sort (owner, 2026-10-15) — also in the URL, and not a filter.
  const explicitSort = documentSortFrom(searchParams);
  const setSort = (sort: DocumentSort) => setSearchParams((prev) => withDocumentSort(sort, prev));
  const parentOf = useMemo(() => categoryParents(categories.data ?? []), [categories.data]);
  // The tree as shown: empty template placeholders ("[Account Name]") dropped,
  // every level in the checklist's order, and the checklist's phases above the
  // categories (owner, 2026-10-15: Phase › Category › Sub-category, as the
  // spreadsheet reads) — lib/folder-phases. Display only.
  const folderView = useMemo(
    () => folderPhaseView(folderViewTree(categories.data ?? []), folderOrder.data),
    [categories.data, folderOrder.data],
  );
  const treeNodes = folderView.nodes;
  const phaseGroups = folderView.phases;
  const nodesById = useMemo(() => categoryNodesById(treeNodes), [treeNodes]);
  // The selected folder (by id, from the URL) and, under it, a period node
  // (Year → Month / Quarter, or Undated) by key — null = the whole folder. The
  // period nodes come from the listing (`@dataroom/shared` lib/category-tree.ts).
  const selectedId = filters.folder;
  const selected: CategoryNode | null = selectedId ? (nodesById.get(selectedId) ?? null) : null;
  const selectedPeriod = filters.period;
  // A phase row: every folder under it (a folder pick replaces it).
  const selectedPhase: FolderPhaseGroup | null =
    !selectedId && filters.phase
      ? (phaseGroups?.find((p) => p.key === filters.phase) ?? null)
      : null;
  const phaseName = selectedPhase?.name ?? null;
  const folderName = selected?.name ?? (categories.isPending ? 'Folder' : 'A folder');
  const periodNodes = useMemo(
    () => periodNodesByCategory(documents.data ?? [], parentOf, branchCategoryIds(treeNodes)),
    [documents.data, parentOf, treeNodes],
  );
  const periodLabel = selectedPeriod
    ? (describePeriodKey(selectedPeriod)?.fullLabel ?? null)
    : null;
  const query = filters.query;
  const searchBox = useDataRoomSearchBox(query, (q, replace) => setFilters({ query: q }, replace));
  // Focus goes to the list after a filter is removed (its ✕ is gone with it).
  const listRef = useRef<HTMLDivElement>(null);
  const focusList = () => listRef.current?.focus();
  // The results area shows ONE thing — the Ask answer or the folder's documents,
  // whichever the person did last (owner, 2026-10-15). Asking clears the folder
  // and every other narrowing (the answer replaces the list); picking a folder,
  // "All documents" or "Show all documents" clears the answer. The answer is not
  // in the URL: any filter arriving later (Back to a folder view) drops it too.
  const filtered = hasDataRoomFilters(filters);
  const [askSort, setAskSort] = useState<DocumentSort | null>(null);
  const ask = useAskTheRoom(id, () => {
    setAskSort(null);
    if (filtered) setFilters(NO_DATA_ROOM_FILTERS);
  });
  const clearAsk = ask.clear;
  useEffect(() => {
    if (filtered) clearAsk();
  }, [filtered, clearAsk]);
  const asking = ask.state.status !== 'idle';
  // Picking a folder starts over: the question and its answer go (owner, 2026-10-22).
  const resetAsk = ask.reset;
  const pickFolder = (patch: Partial<DataRoomFilters>) => {
    resetAsk();
    setFilters(patch);
  };
  const showAll = () => {
    resetAsk();
    if (filtered) setFilters(NO_DATA_ROOM_FILTERS);
    focusList();
  };
  const [uploadOpen, setUploadOpen] = useState(false);
  // "Add documents" opens the upload panel at the top of the page — above the
  // possible-new-version cards — so it brings the panel into view and puts
  // focus on its first control (owner ask 2026-10-15). Clicking it again while
  // the panel is open scrolls back to it.
  const uploadRef = useRef<HTMLDivElement>(null);
  const [uploadReveal, setUploadReveal] = useState(0);
  const openUpload = () => {
    setUploadOpen(true);
    setUploadReveal((n) => n + 1);
  };
  useEffect(() => {
    if (uploadReveal > 0 && uploadRef.current) revealSection(uploadRef.current);
  }, [uploadReveal]);
  // A first-run guide button's target (lib/guide-target.ts): scrolled into view and
  // outlined for a moment. "upload" opens the drop zone first.
  const askRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [glow, setGlow] = useState<GuideTarget | null>(null);
  // "Change folder or date": the Manage document dialog, opened from a row.
  const [managingId, setManagingId] = useState<string | null>(null);
  // The guide's "Move a document or fix its date": the first filed document's
  // Manage dialog, else the list itself.
  const docsRef = useRef<readonly RoomDocument[]>([]);
  docsRef.current = documents.data ?? [];
  const glowTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(glowTimer.current), []);
  useGuideTarget((target) => {
    setGlow(target);
    clearTimeout(glowTimer.current);
    glowTimer.current = setTimeout(() => setGlow(null), 2500);
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const show = (el: HTMLElement | null, focusSelector?: string) => {
      el?.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      if (focusSelector) {
        el?.querySelector<HTMLElement>(focusSelector)?.focus({ preventScroll: true });
      }
    };
    if (target === 'manage') {
      const filed = docsRef.current.find(
        (d) => d.categoryPath.length > 0 && d.aiClassifiable !== false,
      );
      if (filed) setManagingId(filed.id);
      else show(resultsRef.current);
    } else if (target === 'upload') openUpload();
    else if (target === 'ask') show(askRef.current, 'input, textarea');
    else if (target === 'search') show(resultsRef.current, 'input[type="search"]');
    else show(resultsRef.current);
  });
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [editing, setEditing] = useState<RoomDocument | null>(null);
  const modify = useModifyDocumentAttributes(id);
  const modifyError = apiMessage(modify.error, 'Save failed.');
  // Per-document visibility override (ADR-0028): hide from seller / buyers.
  const visibility = useSetDocumentVisibility(id);

  // Per-document classification review (issue #131, ADR-0018). Tracked by id
  // (not a snapshot) so the panel reflects the live classification after the
  // documents query re-polls; same for the in-app viewer.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  // "Open the file" on a file we couldn't read: the viewer starts on the original.
  const [previewOriginal, setPreviewOriginal] = useState(false);
  const uploadReplacement = useUploadReplacement(id);
  const clearUnreadable = useClearUnreadableFlag(id);
  // What the admin can do about a file we couldn't read (ADR-0051 update
  // 2026-10-15): open or download the original, replace it, or clear the flag.
  const unreadableOptionsFor = (doc: RoomDocument, onOpen: (() => void) | undefined) => (
    <UnreadableFileOptions
      document={doc}
      onOpen={doc.downloadReady ? onOpen : undefined}
      downloadHref={doc.downloadReady ? documentDownloadPath(id, doc.id) : undefined}
      onReplace={(body) => uploadReplacement.mutateAsync({ documentId: doc.id, body })}
      onClearFlag={() => clearUnreadable.mutateAsync(doc.id)}
    />
  );
  const pendingProposals = usePendingProposals(id);
  const approveProposal = useApproveProposal(id);
  const rejectProposal = useRejectProposal(id);
  const reclassify = useReclassifyDocument(id);
  // A financial statement's accounting basis (ADR-0053), corrected in the review panel.
  const setBasis = useSetStatementBasis(id);
  const reviewError =
    apiMessage(approveProposal.error, 'Accept failed.') ??
    apiMessage(rejectProposal.error, 'Reject failed.') ??
    apiMessage(reclassify.error, 'Re-classify failed.') ??
    modifyError;
  const resetReviewActions = () => {
    approveProposal.reset();
    rejectProposal.reset();
    reclassify.reset();
    modify.reset();
    setBasis.reset();
  };
  const openReview = (documentId: string) => {
    resetReviewActions();
    setViewingId(documentId);
  };

  // "Needs review" = low confidence + couldn't classify. The room listing is not
  // paginated, so counting over it gives the room's true count.
  const needsReviewOnly = filters.show === 'needs-look';
  // "Couldn't read (N)": files the seller has been asked to send again (ADR-0051).
  const unreadableOnly = filters.show === 'unreadable';
  // The basis facet (ADR-0053): narrow to the statements prepared on one basis.
  const basisFilter: StatementBasis | null = filters.basis;
  const basisCounts = useMemo(() => countStatementBases(documents.data ?? []), [documents.data]);
  const needsReview = useMemo(() => countNeedsReview(documents.data ?? []), [documents.data]);
  const bulkReclassify = useReclassifyNeedsReview(id);
  const [confirmBulk, setConfirmBulk] = useState(false);
  // Live classification status by document id, from the polled listing. Search
  // hits are not polled, so their badges (and the filter) read through this.
  const liveById = useMemo(
    () => new Map((documents.data ?? []).map((d) => [d.id, d] as const)),
    [documents.data],
  );
  const statusOf = (doc: RoomDocument): ClassificationStatus | undefined =>
    liveById.get(doc.id)?.classificationStatus ?? doc.classificationStatus;

  // Free text is a server search; a selected tree node is NOT sent to it: the
  // server's tag facet matches any document CARRYING the tag, but a document also
  // carries edges on other branches (a bank statement carries the checklist's
  // shared "Bank not identified › Bank Statements" level beside its account), so
  // the folder is narrowed below by the tree's own rule (`inFolder`) over the
  // permission-scoped listing — a folder lists exactly its count.
  const searchQuery = { query };
  const searchActive = hasSearchCriteria(searchQuery);
  const search = useDocumentSearch(id, searchQuery);

  if (isUnauthorized(documents.error, search.error)) return <Navigate to="/login" replace />;

  const listPending = searchActive ? search.isPending : documents.isPending;
  const listError = searchActive ? search.error : documents.error;
  const found: RoomDocument[] = searchActive ? (search.data ?? []) : (documents.data ?? []);
  // A period node, "Needs a look", "Couldn't read" and the basis narrow what
  // the listing / search found (search hits carry no tags: the listing's copy).
  const unreadableCount = (documents.data ?? []).filter((d) => d.unreadable).length;
  const { listed, visible: narrowed } = narrowDataRoomDocuments(found, filters, {
    live: (d) => liveById.get(d.id),
    parentOf,
    inPhase: selectedPhase ? (d) => documentInPhase(d, selectedPhase, parentOf) : undefined,
    needsLook: (d) => isNeedsReview(statusOf(d)),
  });
  // Sorted as the URL says; by default newest additions first, and a text search's
  // best match first.
  const sort = explicitSort ?? (query ? null : DEFAULT_DOCUMENT_SORT);
  const visible = sort ? sortDocuments(narrowed, sort) : narrowed;
  const heading = query
    ? 'Search results'
    : selectedId
      ? periodLabel
        ? `${folderName} · ${periodLabel}`
        : folderName
      : (phaseName ?? 'All documents');

  // Look the open document up in the full (polled) listing first, so the review
  // panel stays open — and live — after accepting a suggestion drops the
  // document out of the "Needs review" filter.
  const findDoc = (docId: string | null): RoomDocument | null =>
    docId
      ? ((documents.data ?? []).find((d) => d.id === docId) ??
        listed.find((d) => d.id === docId) ??
        null)
      : null;
  const viewingDoc = findDoc(viewingId);
  const previewDoc = findDoc(previewId);
  const managingDoc = findDoc(managingId);

  // One status per document; the per-row actions live in a ⋯ menu.
  const rowFor = (doc: RoomDocument): DocumentListRow => {
    const cls = statusOf(doc);
    const live = liveById.get(doc.id) ?? doc;
    const reviewable = isNeedsReview(cls);
    // Deal paperwork (not classifiable) cannot be filed — the server refuses it —
    // so its chip is a plain label, not an invitation to file it.
    const opensReview = reviewable;
    const actions: RowMenuItem[] = [
      ...(doc.downloadReady
        ? [{ label: 'View file', icon: 'eye' as const, onSelect: () => setPreviewId(doc.id) }]
        : []),
      { label: 'Review filing', icon: 'folder' as const, onSelect: () => openReview(doc.id) },
      // One place to move a document and change its date; deal paperwork is never
      // filed or dated. Several folders at once is reached from inside it.
      ...(doc.aiClassifiable === false
        ? []
        : [
            {
              label: 'Manage document',
              icon: 'pen' as const,
              onSelect: () => setManagingId(doc.id),
            },
          ]),
      ...visibilityMenuItems(
        live,
        (body) => visibility.mutate({ documentId: doc.id, body }),
        visibility.isPending,
      ),
    ];
    const key = documentStatusKey(
      cls,
      live.status,
      doc.paperworkKind,
      Boolean(live.archive),
      live.processingStatus,
      live.categoryPath.length > 0,
    );
    const indicators = fileIndicatorsOf(live);
    const onStatus = opensReview ? () => openReview(doc.id) : undefined;
    const statusAction = reviewable ? `review the classification of ${doc.name}` : undefined;
    const chip = <StatusChip status={key} onClick={onStatus} actionLabel={statusAction} />;
    return {
      doc,
      live,
      status: (layout) =>
        layout === 'table' ? (
          <div className="flex flex-col items-start gap-1">
            {chip}
            <FileIndicatorsView
              indicators={indicators}
              className="flex-col items-start"
              onReviewRedaction={() => reviewRedaction(doc.id)}
              subject={doc.name}
            />
          </div>
        ) : (
          <>
            {chip}
            <FileIndicatorsView
              indicators={indicators}
              onReviewRedaction={() => reviewRedaction(doc.id)}
              subject={doc.name}
            />
          </>
        ),
      actions,
      // Deal paperwork is never filed or dated: no link for it (as in the viewer).
      onManage: doc.aiClassifiable === false ? undefined : () => setManagingId(doc.id),
      unreadableOptions: live.unreadable
        ? unreadableOptionsFor(live, () => {
            setPreviewOriginal(true);
            setPreviewId(doc.id);
          })
        : undefined,
      openTitle: reviewable ? 'Review classification' : 'View file',
      onOpen: reviewable
        ? () => openReview(doc.id)
        : doc.downloadReady
          ? () => setPreviewId(doc.id)
          : undefined,
    };
  };
  const rows = visible.map(rowFor);
  // The answer's rows: the same rows as the list, in the answer's order until sorted.
  const answered = askAnswerDocuments(ask.state);
  const askRows = (askSort ? sortDocuments(answered, askSort) : answered).map(rowFor);
  // What the results area shows, in words (its title): "Answer: …", "Folder:
  // Financial › Bank Statements › 2024", "All documents".
  const folderPath = selectedId ? categoryPathNames(treeNodes, selectedId) : [];
  const resultsTitle =
    ask.state.status !== 'idle'
      ? `Answer: ${ask.state.question}`
      : query
        ? 'Search results'
        : selectedId
          ? `Folder: ${[...(folderPath.length > 0 ? folderPath : [folderName]), ...(periodLabel ? [periodLabel] : [])].join(' › ')}`
          : phaseName
            ? `Folder: ${phaseName}`
            : 'All documents';
  const listName = ask.state.status !== 'idle' ? `Answer to “${ask.state.question}”` : heading;
  const folderLabel = selectedId
    ? periodLabel
      ? `${folderName} › ${periodLabel}`
      : folderName
    : (phaseName ?? 'All documents');

  // The "Showing: …" bar: one removable chip per filter applied.
  const filterChips = dataRoomFilterChips(
    filters,
    { folderName, periodLabel, phaseName },
    (patch) => {
      setFilters(patch);
      focusList();
    },
  );
  const roomTotal = documents.data ? documents.data.length : null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
      {/* The folder tree (a view over the tag graph); below 1024px it folds
          behind a "Folder: …" button. */}
      <DataRoomFolders
        nodes={treeNodes}
        pending={categories.isPending}
        selectedId={selectedId}
        selectedPeriod={selectedPeriod}
        periodNodes={periodNodes}
        onSelect={(node) => pickFolder({ folder: node?.id ?? null, period: null, phase: null })}
        onSelectPeriod={(node, periodKey) =>
          pickFolder({ folder: node.id, period: periodKey, phase: null })
        }
        label={folderLabel}
        phases={phaseGroups}
        selectedPhase={selectedPhase?.key ?? null}
        onSelectPhase={(phase) => pickFolder({ phase: phase.key, folder: null, period: null })}
        headerAction={
          <span className="inline-flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setNewFolderOpen(true)}>
              New folder
            </Button>
            <HelpTip id="admin.data.new-folder" />
          </span>
        }
        footer={
          // The selected folder's audience (its tier) and "Change audience" —
          // below the scrolling tree, so it is in view whatever the tree's length.
          selected && !SYNTHETIC_CATEGORY_IDS.has(selected.id) ? (
            <FolderAudienceSummary
              key={selected.id}
              roomId={id}
              folderId={selected.id}
              categories={categories.data ?? []}
            />
          ) : undefined
        }
      />

      <div className="min-w-0 space-y-6">
        {uploadOpen ? (
          <div
            ref={uploadRef}
            className={cn(
              'scroll-mt-20 space-y-2 rounded-lg transition-shadow',
              glow === 'upload' && 'ring-2 ring-primary ring-offset-4',
            )}
            data-guide-highlight={glow === 'upload' ? 'true' : undefined}
          >
            <UploadDocumentsCard
              roomId={id}
              onReview={openReview}
              onReviewRedaction={reviewRedaction}
              viewAll={
                // A new tab: leaving this page would end the upload session's list.
                <Link
                  to={`/rooms/${id}/processing`}
                  target="_blank"
                  rel="noopener"
                  className="text-sm font-medium text-primary hover:underline"
                >
                  View all in Processing status
                </Link>
              }
            />
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => setUploadOpen(false)}>
                Close upload
              </Button>
            </div>
          </div>
        ) : null}

        {/* The Processing page's time-left panel, here while documents are still in
            the pipeline or an upload batch is open — gone when the view is left. */}
        <RoomProcessingEstimate roomId={id} documents={documents.data} batchOpen={uploadOpen} />

        {/* "Possible new versions" (ADR-0031): uploads kept separate because they
            were not clearly the same document — a person decides. A one-line
            summary that opens a compact, bulk-actionable list; a file name opens
            the in-app viewer so the two can be compared. */}
        <VersionSuggestionsReview
          roomId={id}
          onOpenDocument={(docId) =>
            liveById.get(docId)?.downloadReady ? setPreviewId(docId) : openReview(docId)
          }
        />

        {/* Ask the Data Room (ADR-0035), and directly below it the ONE results
            area: the answer, or the selected folder's documents (owner, 2026-10-15). */}
        <div className="space-y-3">
          <div
            ref={askRef}
            className={cn(
              'rounded-lg transition-shadow',
              glow === 'ask' && 'ring-2 ring-primary ring-offset-4',
            )}
            data-guide-highlight={glow === 'ask' ? 'true' : undefined}
          >
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>
                  Ask the Data Room <HelpTip id="admin.data.ask" className="ml-1" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AskTheRoomPrompt ask={ask} />
              </CardContent>
            </Card>
          </div>

          <div
            ref={resultsRef}
            className={cn(
              'rounded-lg transition-shadow',
              (glow === 'documents' || glow === 'search') && 'ring-2 ring-primary ring-offset-4',
            )}
            data-guide-highlight={glow === 'documents' || glow === 'search' ? 'true' : undefined}
          >
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <ResultsTitle
                    title={resultsTitle}
                    onClear={asking || filtered ? showAll : undefined}
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/rooms/${id}/processing`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Processing status
                    </Link>
                    <span className="inline-flex items-center gap-1">
                      <Button size="sm" onClick={openUpload}>
                        <Icon name="upload" />
                        Add documents
                      </Button>
                      <HelpTip id="admin.data.add-documents" />
                    </span>
                  </div>
                </div>
                {asking ? (
                  <div className="pt-2" aria-live="polite">
                    <AskAnswerSummary state={ask.state} />
                  </div>
                ) : (
                  <>
                    <div className="flex items-start gap-2 pt-2">
                      <SearchBar
                        key={searchBox.key}
                        initialQuery={query}
                        onQueryChange={searchBox.onQueryChange}
                        searching={search.isFetching}
                        className="min-w-0 flex-1"
                      />
                      <DataRoomFilterMenu
                        basisCounts={basisCounts}
                        basis={basisFilter}
                        onBasisChange={(basis) => setFilters({ basis })}
                      />
                      {/* The menu renders nothing until a statement basis exists. */}
                      {Object.values(basisCounts).some((n) => n > 0) || basisFilter ? (
                        <HelpTip id="admin.data.filter-menu" className="mt-2.5" />
                      ) : null}
                    </div>
                    {needsReview.total > 0 || needsReviewOnly ? (
                      <NeedsReviewBar
                        reviewHref={`/rooms/${id}/review`}
                        counts={needsReview}
                        active={needsReviewOnly}
                        onToggle={() => setFilters({ show: needsReviewOnly ? null : 'needs-look' })}
                        confirming={confirmBulk}
                        running={bulkReclassify.isPending}
                        onAskConfirm={() => {
                          bulkReclassify.reset();
                          setConfirmBulk(true);
                        }}
                        onCancel={() => setConfirmBulk(false)}
                        onConfirm={() =>
                          bulkReclassify.mutate(needsReview, {
                            onSettled: () => setConfirmBulk(false),
                          })
                        }
                      />
                    ) : null}
                    <div className="flex items-center gap-1">
                      <UnreadableFilterChip
                        count={unreadableCount}
                        active={unreadableOnly}
                        onToggle={() => setFilters({ show: unreadableOnly ? null : 'unreadable' })}
                        title="Files we couldn't read — the seller has been asked for a readable copy"
                      />
                      {unreadableCount > 0 || unreadableOnly ? (
                        <HelpTip id="admin.data.unreadable" className="mt-2" />
                      ) : null}
                    </div>
                    {bulkReclassify.isError ? (
                      <p className="pt-2 text-sm text-destructive" role="alert">
                        {apiMessage(bulkReclassify.error, 'Re-run failed.')}
                      </p>
                    ) : bulkReclassify.isSuccess && !confirmBulk ? (
                      <p className="pt-2 text-sm text-muted-foreground" role="status">
                        Re-running the AI on {plural(bulkReclassify.data.enqueued, 'document')} —
                        they show Processing until the AI finishes.
                        {bulkReclassify.data.truncated
                          ? ' More remain; run it again when these finish.'
                          : ''}
                      </p>
                    ) : null}
                    <ActiveFiltersBar
                      chips={filterChips}
                      shown={listPending || listError ? null : rows.length}
                      total={roomTotal}
                      onShowAll={showAll}
                    />
                  </>
                )}
                {visibility.isError ? (
                  <p className="pt-2 text-sm text-destructive" role="alert">
                    {apiMessage(visibility.error, 'Changing who can see the document failed.')}
                  </p>
                ) : null}
              </CardHeader>
              <CardContent
                ref={listRef}
                tabIndex={-1}
                role="region"
                aria-label={`${listName}: document list`}
                className="outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring max-md:px-3"
              >
                {asking ? (
                  askRows.length > 0 ? (
                    <DocumentList rows={askRows} sort={askSort} onSort={setAskSort} />
                  ) : null
                ) : listPending ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : listError ? (
                  <p className="text-sm text-destructive">{listError.message}</p>
                ) : rows.length > 0 ? (
                  <DocumentList rows={rows} sort={sort} onSort={setSort} />
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {basisFilter && listed.length > 0
                        ? 'No documents here match that accounting basis.'
                        : needsReviewOnly && listed.length > 0
                          ? 'Nothing here needs a look.'
                          : unreadableOnly && listed.length > 0
                            ? 'Every file here could be read.'
                            : searchActive
                              ? 'No documents match your search.'
                              : selectedId
                                ? 'No documents in this folder.'
                                : 'No documents yet. Upload files and the AI files each one into the right folder.'}
                    </p>
                    {filtered ? (
                      <Button size="sm" onClick={showAll}>
                        <Icon name="close" size={14} />
                        Show all documents
                      </Button>
                    ) : null}
                    {!searchActive && !selectedId && !uploadOpen && listed.length === 0 ? (
                      <Button variant="outline" size="sm" onClick={openUpload}>
                        Add the first documents
                      </Button>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {newFolderOpen ? (
        <NewFolderDialog
          roomId={id}
          categories={categories.data ?? []}
          onClose={() => setNewFolderOpen(false)}
        />
      ) : null}

      {/* Modify attributes — the admin reassigns the document's category tags. */}
      <ModifyDocumentAttributesDialog
        document={editing}
        categories={categories.data ?? []}
        pending={modify.isPending}
        error={modifyError}
        onClose={() => setEditing(null)}
        onSubmit={(categoryTagIds) => {
          if (!editing) return;
          modify.mutate(
            { documentId: editing.id, body: { categoryTagIds } },
            { onSuccess: () => setEditing(null) },
          );
        }}
      />

      {/* Classification review (issue #131, ADR-0018) — the AI's suggestions with
          confidence (accept/reject), a manual category pick, the viewer and
          re-classify, all for one document. */}
      {viewingDoc ? (
        <DocumentDetailModal
          key={viewingDoc.id}
          document={viewingDoc}
          categories={categories.data ?? []}
          phases={folderOrder.data?.phases}
          proposals={pendingProposals.data ?? []}
          proposalsLoading={pendingProposals.isPending}
          approvingId={approveProposal.isPending ? (approveProposal.variables ?? null) : null}
          rejectingId={rejectProposal.isPending ? (rejectProposal.variables ?? null) : null}
          reclassifying={reclassify.isPending}
          savingCategories={modify.isPending}
          actionError={reviewError}
          onApprove={(proposalId) => {
            resetReviewActions();
            approveProposal.mutate(proposalId);
          }}
          onReject={(proposalId) => {
            resetReviewActions();
            rejectProposal.mutate(proposalId);
          }}
          onReclassify={() => {
            resetReviewActions();
            reclassify.mutate(viewingDoc.id);
          }}
          onSetCategories={(categoryTagIds, onSaved) => {
            resetReviewActions();
            modify.mutate(
              { documentId: viewingDoc.id, body: { categoryTagIds } },
              { onSuccess: onSaved },
            );
          }}
          onSetStatementBasis={(basis, onSaved) => {
            resetReviewActions();
            setBasis.mutate({ documentId: viewingDoc.id, body: { basis } }, { onSuccess: onSaved });
          }}
          savingBasis={setBasis.isPending}
          basisError={apiMessage(setBasis.error, 'Saving the basis failed.')}
          onOpenPreview={() => setPreviewId(viewingDoc.id)}
          onReviewRedaction={() => reviewRedaction(viewingDoc.id)}
          versionSuggestions={
            <VersionSuggestionsPanel
              roomId={id}
              documentId={viewingDoc.id}
              onOpenDocument={openReview}
            />
          }
          versionHistory={
            viewingDoc.downloadReady ? (
              <DocumentVersionHistory roomId={id} documentId={viewingDoc.id} />
            ) : null
          }
          reviewQueueHref={
            isNeedsReview(viewingDoc.classificationStatus)
              ? `/rooms/${id}/review?doc=${encodeURIComponent(viewingDoc.id)}`
              : undefined
          }
          onClose={() => {
            setViewingId(null);
            resetReviewActions();
          }}
        />
      ) : null}

      {/* "Change folder or date" — the same Manage document dialog the viewer opens. */}
      {managingDoc ? (
        <ManageDocumentDialog
          key={managingDoc.id}
          roomId={id}
          document={managingDoc}
          onClose={() => setManagingId(null)}
          onEditTags={() => {
            modify.reset();
            setEditing(managingDoc);
            setManagingId(null);
          }}
        />
      ) : null}

      {/* The in-app document viewer: fetch the bytes credentialed and render them
          inline so the admin can actually SEE the file. */}
      {previewDoc ? (
        <DocumentPreviewModal
          key={previewDoc.id}
          roomId={id}
          document={previewDoc}
          onClose={() => {
            setPreviewId(null);
            setPreviewOriginal(false);
          }}
          onReviewRedaction={(d) => reviewRedaction(d.id)}
          canManage
          original={previewOriginal}
          renderUnreadableOptions={(openOriginal) => unreadableOptionsFor(previewDoc, openOriginal)}
        />
      ) : null}
    </div>
  );
}

interface NeedsReviewBarProps {
  /** The full-page review queue; when set, a "Review one by one" link leads there. */
  reviewHref?: string;
  counts: NeedsReviewCounts;
  active: boolean;
  onToggle: () => void;
  confirming: boolean;
  running: boolean;
  onAskConfirm: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/** The "Needs review (N)" filter chip plus the bulk "Re-run AI on N documents"
 * action with its short inline confirm. Exported for the render tests. */
export function NeedsReviewBar({
  reviewHref,
  counts,
  active,
  onToggle,
  confirming,
  running,
  onAskConfirm,
  onCancel,
  onConfirm,
}: NeedsReviewBarProps) {
  const docs = plural(counts.total, 'document');
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
      <span className="inline-flex items-center gap-1.5">
        <button
          type="button"
          aria-pressed={active}
          onClick={onToggle}
          title={
            active
              ? 'Remove this filter and show all documents again'
              : 'Documents the AI filed with low confidence or could not file'
          }
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-pill border px-3 text-[13px] font-medium transition-colors',
            active
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input bg-surface text-ink-2 hover:bg-surface-2',
          )}
        >
          <Icon name="eye" size={14} />
          Needs a look ({counts.total}){active ? <ActiveChipClose /> : null}
        </button>
        <HelpTip id="admin.data.needs-a-look" />
      </span>
      {reviewHref && counts.total > 0 ? (
        <Link
          to={reviewHref}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Review one by one
          <Icon name="chevronRight" size={14} />
        </Link>
      ) : null}
      {counts.total > 0 ? (
        confirming ? (
          <div
            role="group"
            aria-label="Confirm re-running the AI"
            className="flex flex-wrap items-center gap-2 text-xs"
          >
            <span>Re-run the AI on {docs}? Categories you set yourself are kept.</span>
            <Button size="sm" disabled={running} onClick={onConfirm}>
              {running ? 'Starting…' : 'Re-run'}
            </Button>
            <Button size="sm" variant="ghost" disabled={running} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={onAskConfirm}>
            <Icon name="refresh" size={14} />
            Re-run AI on {docs}
          </Button>
        )
      ) : null}
    </div>
  );
}
